import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../UIElements/Icon";
import { ContextHelp } from "../../UIElements/ContextHelp";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardArrow,
} from "../../UIElements/HoverCard";

export const StatCard = ({
  title,
  titleHelp = null,
  value,
  change,
  icon,
  yesterday,
  gradient = false,
  valueColor = "text-foreground",
  iconColor = "text-primary",
  iconBg = "bg-accent",
  className = "",
  changeColor = null,
}) => {
  const { t } = useTranslation("common");
  const ChangeElement = () => {
    const changeElement = change ? (
      <p
        className={`text-sm ${
          changeColor ??
          (parseFloat(change) === 0
            ? "text-warning"
            : change.startsWith("+")
              ? "text-success"
              : "text-error")
        }`}
      >
        {change}
      </p>
    ) : (
      <div className="h-5 text-sm" aria-hidden="true"></div>
    );

    if (change && yesterday !== undefined && yesterday !== null) {
      return (
        <HoverCard openDelay={200} closeDelay={100}>
          <HoverCardTrigger asChild>
            <div className="cursor-pointer">{changeElement}</div>
          </HoverCardTrigger>
          <HoverCardContent
            side="bottom"
            align="center"
            className="border-border bg-popover text-popover-foreground w-40 border shadow-[var(--od-shadow-2)]"
          >
            <HoverCardArrow className="fill-popover" />
            <div className="">
              <div className="flex items-center space-x-2">
                <div className="bg-accent rounded-md">
                  <Icon name="Calendar" className="text-primary h-4 w-4" />
                </div>
                <div className="flex-1">
                  <p className="text-muted-foreground text-xs">
                    {t("date.previousPeriod")}
                  </p>
                  <p className={`text-lg font-bold ${valueColor}`}>
                    {yesterday}
                  </p>
                </div>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    }

    return changeElement;
  };

  return (
    <div
      className={`${
        gradient ? "border-primary/30 bg-primary/10" : "border-border bg-card"
      } text-card-foreground rounded-lg border p-4 shadow-[var(--od-shadow-1)] ${className}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-muted-foreground mb-1 flex items-center gap-1 text-sm">
            <span>{title}</span>
            {titleHelp ? <ContextHelp {...titleHelp} /> : null}
          </p>
          <div className={`text-2xl font-semibold ${valueColor} mb-1`}>
            {value}
          </div>
          <ChangeElement />
        </div>
        {icon && (
          <div className={`p-3 ${iconBg} rounded-lg`}>
            <Icon name={icon} className={`h-6 w-6 ${iconColor}`} />
          </div>
        )}
      </div>
    </div>
  );
};

export const StatsGrid = ({ stats, columns = 3, className = "" }) => {
  const getGridClass = () => {
    switch (columns) {
      case 1:
        return "grid grid-cols-1 gap-3 lg:gap-4";
      case 2:
        return "grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4";
      case 3:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4";
      case 4:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4";
      case 5:
        return "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4";
      case 6:
        return "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4";
      default:
        return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4";
    }
  };

  return (
    <div className={`${getGridClass()} ${className}`}>
      {stats.map((stat, index) => (
        <StatCard key={stat.id || index} {...stat} />
      ))}
    </div>
  );
};
